import React, { useEffect, useState } from 'react';
import { useAdminApiService } from '../../hooks/useAdminApiService';

const AdminSecuritySettings: React.FC = () => {
  const { getSecuritySettings, updateSecuritySettings } = useAdminApiService();

  const [values, setValues] = useState({
    minPasswordLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumbers: true,
    requireSpecialChars: true,
    passwordExpiryDays: 90,
    lockoutEnabled: true,
    maxLoginAttempts: 3,
    lockoutDurationMinutes: 60,
    loginRateLimitEnabled: true,
    loginRateLimitPerIpPerHour: 30,
    loginRateLimitPerUserPerHour: 15,
    blocklistEnabled: true,
    blocklistEnforcement: 'enforce',
    emailMultiIpAutoBlockEnabled: false,
    emailMultiIpWindowMinutes: 15,
    emailMultiIpMinDistinctIps: 3,
    unverifiedEmailAbuseAutoBlockEnabled: false,
    unverifiedEmailAbuseWindowMinutes: 15,
    unverifiedEmailAbuseMinHits: 5,
    geoChallengeEnabled: false,
    deviceChallengeEnabled: false,
    registrationRateLimitEnabled: true,
    registrationPerIpPerHour: 20,
    registrationPerEmailPerHour: 5,
    verificationPerIpPerHour: 10,
    disposableEmailDomainsEnabled: true,
    mfaEnabled: true,
    mfaTimeoutMinutes: 10
  });

  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await getSecuritySettings();
        if (!cancelled && response.success && response.data) {
          const data = response.data;
          setValues(prev => ({
            ...prev,
            lockoutEnabled: data.lockoutEnabled ?? prev.lockoutEnabled,
            maxLoginAttempts: data.maxLoginAttempts ?? prev.maxLoginAttempts,
            lockoutDurationMinutes: data.lockoutDurationMinutes ?? prev.lockoutDurationMinutes,
            loginRateLimitEnabled: data.loginRateLimitEnabled ?? prev.loginRateLimitEnabled,
            loginRateLimitPerIpPerHour: data.loginRateLimitPerIpPerHour ?? prev.loginRateLimitPerIpPerHour,
            loginRateLimitPerUserPerHour: data.loginRateLimitPerUserPerHour ?? prev.loginRateLimitPerUserPerHour,
            blocklistEnabled: data.blocklistEnabled ?? prev.blocklistEnabled,
            blocklistEnforcement: data.blocklistEnforcement ?? prev.blocklistEnforcement,
            emailMultiIpAutoBlockEnabled: data.emailMultiIpAutoBlockEnabled ?? prev.emailMultiIpAutoBlockEnabled,
            emailMultiIpWindowMinutes: data.emailMultiIpWindowMinutes ?? prev.emailMultiIpWindowMinutes,
            emailMultiIpMinDistinctIps: data.emailMultiIpMinDistinctIps ?? prev.emailMultiIpMinDistinctIps,
            unverifiedEmailAbuseAutoBlockEnabled: data.unverifiedEmailAbuseAutoBlockEnabled ?? prev.unverifiedEmailAbuseAutoBlockEnabled,
            unverifiedEmailAbuseWindowMinutes: data.unverifiedEmailAbuseWindowMinutes ?? prev.unverifiedEmailAbuseWindowMinutes,
            unverifiedEmailAbuseMinHits: data.unverifiedEmailAbuseMinHits ?? prev.unverifiedEmailAbuseMinHits,
            geoChallengeEnabled: data.geoChallengeEnabled ?? prev.geoChallengeEnabled,
            deviceChallengeEnabled: data.deviceChallengeEnabled ?? prev.deviceChallengeEnabled,
            registrationRateLimitEnabled: data.registrationRateLimitEnabled ?? prev.registrationRateLimitEnabled,
            registrationPerIpPerHour: data.registrationPerIpPerHour ?? prev.registrationPerIpPerHour,
            registrationPerEmailPerHour: data.registrationPerEmailPerHour ?? prev.registrationPerEmailPerHour,
            verificationPerIpPerHour: data.verificationPerIpPerHour ?? prev.verificationPerIpPerHour,
            disposableEmailDomainsEnabled: data.disposableEmailDomainsEnabled ?? prev.disposableEmailDomainsEnabled
          }));
        }
      } catch (error) {
        if (!cancelled) {
          setSaveMessage('Failed to load security settings. Showing defaults.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getSecuritySettings]);

  const handleInputChange = (field: string, value: boolean | number | string) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      const response = await updateSecuritySettings({
        lockoutEnabled: values.lockoutEnabled,
        maxLoginAttempts: values.maxLoginAttempts,
        lockoutDurationMinutes: values.lockoutDurationMinutes,
        loginRateLimitEnabled: values.loginRateLimitEnabled,
        loginRateLimitPerIpPerHour: values.loginRateLimitPerIpPerHour,
        loginRateLimitPerUserPerHour: values.loginRateLimitPerUserPerHour,
        blocklistEnabled: values.blocklistEnabled,
        blocklistEnforcement: values.blocklistEnforcement,
        emailMultiIpAutoBlockEnabled: values.emailMultiIpAutoBlockEnabled,
        emailMultiIpWindowMinutes: values.emailMultiIpWindowMinutes,
        emailMultiIpMinDistinctIps: values.emailMultiIpMinDistinctIps,
        unverifiedEmailAbuseAutoBlockEnabled: values.unverifiedEmailAbuseAutoBlockEnabled,
        unverifiedEmailAbuseWindowMinutes: values.unverifiedEmailAbuseWindowMinutes,
        unverifiedEmailAbuseMinHits: values.unverifiedEmailAbuseMinHits,
        geoChallengeEnabled: values.geoChallengeEnabled,
        deviceChallengeEnabled: values.deviceChallengeEnabled,
        registrationRateLimitEnabled: values.registrationRateLimitEnabled,
        registrationPerIpPerHour: values.registrationPerIpPerHour,
        registrationPerEmailPerHour: values.registrationPerEmailPerHour,
        verificationPerIpPerHour: values.verificationPerIpPerHour,
        disposableEmailDomainsEnabled: values.disposableEmailDomainsEnabled
      });

      if (!response.success) {
        throw new Error(response.error || 'Save failed');
      }

      if (response.data) {
        const data = response.data;
        setValues(prev => ({
          ...prev,
          lockoutEnabled: data.lockoutEnabled ?? prev.lockoutEnabled,
          maxLoginAttempts: data.maxLoginAttempts ?? prev.maxLoginAttempts,
          lockoutDurationMinutes: data.lockoutDurationMinutes ?? prev.lockoutDurationMinutes,
          loginRateLimitEnabled: data.loginRateLimitEnabled ?? prev.loginRateLimitEnabled,
          loginRateLimitPerIpPerHour: data.loginRateLimitPerIpPerHour ?? prev.loginRateLimitPerIpPerHour,
          loginRateLimitPerUserPerHour: data.loginRateLimitPerUserPerHour ?? prev.loginRateLimitPerUserPerHour,
          blocklistEnabled: data.blocklistEnabled ?? prev.blocklistEnabled,
          blocklistEnforcement: data.blocklistEnforcement ?? prev.blocklistEnforcement,
          emailMultiIpAutoBlockEnabled: data.emailMultiIpAutoBlockEnabled ?? prev.emailMultiIpAutoBlockEnabled,
          emailMultiIpWindowMinutes: data.emailMultiIpWindowMinutes ?? prev.emailMultiIpWindowMinutes,
          emailMultiIpMinDistinctIps: data.emailMultiIpMinDistinctIps ?? prev.emailMultiIpMinDistinctIps,
          unverifiedEmailAbuseAutoBlockEnabled: data.unverifiedEmailAbuseAutoBlockEnabled ?? prev.unverifiedEmailAbuseAutoBlockEnabled,
          unverifiedEmailAbuseWindowMinutes: data.unverifiedEmailAbuseWindowMinutes ?? prev.unverifiedEmailAbuseWindowMinutes,
          unverifiedEmailAbuseMinHits: data.unverifiedEmailAbuseMinHits ?? prev.unverifiedEmailAbuseMinHits,
          geoChallengeEnabled: data.geoChallengeEnabled ?? prev.geoChallengeEnabled,
          deviceChallengeEnabled: data.deviceChallengeEnabled ?? prev.deviceChallengeEnabled,
          registrationRateLimitEnabled: data.registrationRateLimitEnabled ?? prev.registrationRateLimitEnabled,
          registrationPerIpPerHour: data.registrationPerIpPerHour ?? prev.registrationPerIpPerHour,
          registrationPerEmailPerHour: data.registrationPerEmailPerHour ?? prev.registrationPerEmailPerHour,
          verificationPerIpPerHour: data.verificationPerIpPerHour ?? prev.verificationPerIpPerHour,
          disposableEmailDomainsEnabled: data.disposableEmailDomainsEnabled ?? prev.disposableEmailDomainsEnabled
        }));
      }

      setSaveMessage('Security settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error: any) {
      setSaveMessage(error?.message || 'Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ 
      maxWidth: 800, 
      margin: '32px auto', 
      padding: '32px',
      background: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #e5e7eb'
    }}>
      <div style={{ 
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #f3f4f6'
      }}>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          color: '#1f2937',
          fontSize: '28px',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Security Settings
          </span>
        </h2>
        <p style={{ 
          margin: 0, 
          color: '#6b7280', 
          fontSize: '16px',
          lineHeight: '1.5'
        }}>
          Configure login lockout and rate limits. Password policy fields are UI-only for now.
        </p>
      </div>

      {loading && (
        <p style={{ color: '#6b7280', marginBottom: '24px' }}>Loading security settings...</p>
      )}

      {/* Password Policy */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '600'
        }}>
          Password Policy (display only)
        </h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '24px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Minimum Password Length
            </label>
            <input
              type="number"
              min={6}
              max={32}
              value={values.minPasswordLength}
              onChange={e => handleInputChange('minPasswordLength', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Password Expiry (days)
            </label>
            <input
              type="number"
              min={30}
              max={365}
              value={values.passwordExpiryDays}
              onChange={e => handleInputChange('passwordExpiryDays', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px',
          marginTop: '20px'
        }}>
          {[
            { key: 'requireUppercase', label: 'Require Uppercase' },
            { key: 'requireLowercase', label: 'Require Lowercase' },
            { key: 'requireNumbers', label: 'Require Numbers' },
            { key: 'requireSpecialChars', label: 'Require Special Characters' }
          ].map(item => (
            <div key={item.key} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              background: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <input
                type="checkbox"
                checked={values[item.key as keyof typeof values] as boolean}
                onChange={e => handleInputChange(item.key, e.target.checked)}
                style={{ 
                  width: '18px',
                  height: '18px',
                  accentColor: '#3b82f6'
                }}
              />
              <label style={{ 
                fontWeight: '500',
                color: '#374151',
                fontSize: '14px',
                margin: 0,
                cursor: 'pointer'
              }}>
                {item.label}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Login Security */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        borderRadius: '12px',
        border: '1px solid #fbbf24'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '600'
        }}>
          Login Security
        </h3>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px',
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          marginBottom: '20px'
        }}>
          <input
            type="checkbox"
            checked={values.lockoutEnabled}
            onChange={e => handleInputChange('lockoutEnabled', e.target.checked)}
            style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
          />
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px', margin: 0 }}>
            Enable account lockout (kill-switch)
          </label>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '24px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Max Login Attempts
            </label>
            <input
              type="number"
              min={1}
              max={20}
              value={values.maxLoginAttempts}
              onChange={e => handleInputChange('maxLoginAttempts', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Lockout Duration (minutes)
            </label>
            <input
              type="number"
              min={1}
              max={1440}
              value={values.lockoutDurationMinutes}
              onChange={e => handleInputChange('lockoutDurationMinutes', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px',
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          marginTop: '20px',
          marginBottom: '20px'
        }}>
          <input
            type="checkbox"
            checked={values.loginRateLimitEnabled}
            onChange={e => handleInputChange('loginRateLimitEnabled', e.target.checked)}
            style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
          />
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px', margin: 0 }}>
            Enable login rate limiting
          </label>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '24px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Max attempts per IP / hour
            </label>
            <input
              type="number"
              min={1}
              max={1000}
              value={values.loginRateLimitPerIpPerHour}
              onChange={e => handleInputChange('loginRateLimitPerIpPerHour', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Max attempts per user / hour
            </label>
            <input
              type="number"
              min={1}
              max={1000}
              value={values.loginRateLimitPerUserPerHour}
              onChange={e => handleInputChange('loginRateLimitPerUserPerHour', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      </div>

      {/* Blocklist + Auto-block */}
      <div style={{
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        borderRadius: '12px',
        border: '1px solid #93c5fd'
      }}>
        <h3 style={{ margin: '0 0 20px 0', color: '#1e293b', fontSize: '20px', fontWeight: '600' }}>
          Blocklist &amp; Auto-block
        </h3>

        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px',
          background: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '16px'
        }}>
          <input
            type="checkbox"
            checked={values.blocklistEnabled}
            onChange={e => handleInputChange('blocklistEnabled', e.target.checked)}
            style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
          />
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px', margin: 0 }}>
            Enable security blocklist (IP / email / phone)
          </label>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
            Enforcement mode
          </label>
          <select
            value={values.blocklistEnforcement}
            onChange={e => handleInputChange('blocklistEnforcement', e.target.value as any)}
            style={{ width: '100%', maxWidth: 280, padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px' }}
          >
            <option value="enforce">enforce (block)</option>
            <option value="log_only">log_only (observe only)</option>
          </select>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px',
          background: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '16px'
        }}>
          <input
            type="checkbox"
            checked={values.emailMultiIpAutoBlockEnabled}
            onChange={e => handleInputChange('emailMultiIpAutoBlockEnabled', e.target.checked)}
            style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
          />
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px', margin: 0 }}>
            Auto-block: same email from many IPs (default OFF)
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
              Multi-IP window (minutes)
            </label>
            <input
              type="number"
              min={1}
              max={1440}
              value={values.emailMultiIpWindowMinutes}
              onChange={e => handleInputChange('emailMultiIpWindowMinutes', Number(e.target.value))}
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
              Min distinct IPs
            </label>
            <input
              type="number"
              min={2}
              max={50}
              value={values.emailMultiIpMinDistinctIps}
              onChange={e => handleInputChange('emailMultiIpMinDistinctIps', Number(e.target.value))}
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
            />
          </div>
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px',
          background: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '16px'
        }}>
          <input
            type="checkbox"
            checked={values.unverifiedEmailAbuseAutoBlockEnabled}
            onChange={e => handleInputChange('unverifiedEmailAbuseAutoBlockEnabled', e.target.checked)}
            style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
          />
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px', margin: 0 }}>
            Auto-block: unverified-email abuse (default OFF)
          </label>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
              Unverified abuse window (minutes)
            </label>
            <input
              type="number"
              min={1}
              max={1440}
              value={values.unverifiedEmailAbuseWindowMinutes}
              onChange={e => handleInputChange('unverifiedEmailAbuseWindowMinutes', Number(e.target.value))}
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
              Min hits
            </label>
            <input
              type="number"
              min={2}
              max={100}
              value={values.unverifiedEmailAbuseMinHits}
              onChange={e => handleInputChange('unverifiedEmailAbuseMinHits', Number(e.target.value))}
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
            />
          </div>
        </div>
      </div>

      {/* Geo / Device challenges */}
      <div style={{
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)',
        borderRadius: '12px',
        border: '1px solid #e9d5ff'
      }}>
        <h3 style={{ margin: '0 0 12px 0', color: '#1e293b', fontSize: '20px', fontWeight: '600' }}>
          Login location &amp; device challenges
        </h3>
        <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '13px' }}>
          Both default OFF. When enabled, mismatched geolocation returns 462 and mismatched device returns 460 after password/OTP succeed. Geo lookup failures fail-open.
        </p>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px',
          background: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '12px'
        }}>
          <input
            type="checkbox"
            checked={values.geoChallengeEnabled}
            onChange={e => handleInputChange('geoChallengeEnabled', e.target.checked)}
            style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
          />
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px', margin: 0 }}>
            Enable geolocation challenge (462)
          </label>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px',
          background: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb'
        }}>
          <input
            type="checkbox"
            checked={values.deviceChallengeEnabled}
            onChange={e => handleInputChange('deviceChallengeEnabled', e.target.checked)}
            style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
          />
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px', margin: 0 }}>
            Enable new-device challenge (460)
          </label>
        </div>
      </div>

      {/* Registration abuse */}
      <div style={{
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
        borderRadius: '12px',
        border: '1px solid #fdba74'
      }}>
        <h3 style={{ margin: '0 0 16px 0', color: '#1e293b', fontSize: '20px', fontWeight: '600' }}>
          Registration abuse prevention
        </h3>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px',
          background: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '12px'
        }}>
          <input
            type="checkbox"
            checked={values.registrationRateLimitEnabled}
            onChange={e => handleInputChange('registrationRateLimitEnabled', e.target.checked)}
            style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
          />
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px', margin: 0 }}>
            Enable registration / verification rate limits
          </label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
              Reg per IP / hour
            </label>
            <input
              type="number"
              min={1}
              max={1000}
              value={values.registrationPerIpPerHour}
              onChange={e => handleInputChange('registrationPerIpPerHour', Number(e.target.value))}
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
              Reg per email / hour
            </label>
            <input
              type="number"
              min={1}
              max={100}
              value={values.registrationPerEmailPerHour}
              onChange={e => handleInputChange('registrationPerEmailPerHour', Number(e.target.value))}
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
              Verify per IP / hour
            </label>
            <input
              type="number"
              min={1}
              max={1000}
              value={values.verificationPerIpPerHour}
              onChange={e => handleInputChange('verificationPerIpPerHour', Number(e.target.value))}
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '16px', boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', padding: '12px',
          background: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb'
        }}>
          <input
            type="checkbox"
            checked={values.disposableEmailDomainsEnabled}
            onChange={e => handleInputChange('disposableEmailDomainsEnabled', e.target.checked)}
            style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
          />
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px', margin: 0 }}>
            Reject disposable email domains (mailinator, yopmail, etc.)
          </label>
        </div>
      </div>

      {/* MFA Settings */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
        borderRadius: '12px',
        border: '1px solid #d1fae5'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '600'
        }}>
          Multi-Factor Authentication (display only)
        </h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px',
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <input
              type="checkbox"
              checked={values.mfaEnabled}
              onChange={e => handleInputChange('mfaEnabled', e.target.checked)}
              style={{ 
                width: '20px',
                height: '20px',
                accentColor: '#10b981'
              }}
            />
            <div>
              <label style={{ 
                fontWeight: '600',
                color: '#374151',
                fontSize: '16px',
                margin: 0,
                cursor: 'pointer'
              }}>
                Enable MFA for Admin Users
              </label>
              <div style={{ 
                fontSize: '13px', 
                color: '#6b7280',
                marginTop: '4px'
              }}>
                Per-user MFA is managed on each admin profile
              </div>
            </div>
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              MFA Timeout (minutes)
            </label>
            <input
              type="number"
              min={5}
              max={60}
              value={values.mfaTimeoutMinutes}
              onChange={e => handleInputChange('mfaTimeoutMinutes', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '32px',
        padding: '24px',
        background: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <button
          onClick={handleSave}
          disabled={isSaving || loading}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '16px 32px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isSaving || loading ? 'not-allowed' : 'pointer',
            opacity: isSaving || loading ? 0.7 : 1,
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {isSaving ? 'Saving...' : 'Save Security Settings'}
        </button>
        
        {saveMessage && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: saveMessage.includes('successfully') ? '#065f46' : '#991b1b',
            background: saveMessage.includes('successfully') ? '#d1fae5' : '#fee2e2',
            border: `1px solid ${saveMessage.includes('successfully') ? '#a7f3d0' : '#fecaca'}`
          }}>
            {saveMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSecuritySettings;
